
import { User, LogOut, Key, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { UserLevelBadge } from "@/components/ui/user-level-badge";
import { useUserLevel } from "@/hooks/useUserLevel";

interface UserAvatarMenuProps {
  onResetPassword?: () => void;
  onDeleteAccount?: () => void;
}

const UserAvatarMenu = ({ onResetPassword, onDeleteAccount }: UserAvatarMenuProps) => {
  const { user, signOut } = useAuth();
  const { userLevel, isLoading } = useUserLevel();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    // No need to navigate here - AuthContext handles the redirect
  };

  if (!user) return null;

  const initials = user.email?.slice(0, 2).toUpperCase() || "U";
  const currentLevel = userLevel?.current_level || 'Inactive';
  const levelNumber = userLevel?.level_number || 0;

  const renderAvatarWithLevelShape = () => {
    const baseClasses = "h-10 w-10 text-sm font-medium flex items-center justify-center text-white relative";
    
    if (currentLevel === 'Inactive') {
      // Outline orange circle for Inactive
      return (
        <div 
          className={`${baseClasses} rounded-full border-2 bg-transparent text-orange-500`}
          style={{ borderColor: '#FF6B00' }}
        >
          {initials}
        </div>
      );
    }
    
    if (currentLevel === 'Saver') {
      // Filled teal circle for Saver
      return (
        <div 
          className={`${baseClasses} rounded-full`}
          style={{ backgroundColor: '#00F6C5' }}
        >
          {initials}
        </div>
      );
    }
    
    if (currentLevel === 'Organizer') {
      // Blue square for Organizer
      return (
        <div 
          className={`${baseClasses} rounded-sm`}
          style={{ backgroundColor: '#4EB0FF' }}
        >
          {initials}
        </div>
      );
    }
    
    if (currentLevel === 'Discoverer') {
      // Purple pentagon for Discoverer
      return (
        <div 
          className={`${baseClasses}`}
          style={{
            backgroundColor: '#B164F8',
            clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)'
          }}
        >
          {initials}
        </div>
      );
    }
    
    if (currentLevel === 'Curator') {
      // Gradient star for Curator
      return (
        <div 
          className={`${baseClasses}`}
          style={{
            background: 'linear-gradient(135deg, #0073FF 0%, #FF00A4 100%)',
            clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
          }}
        >
          {initials}
        </div>
      );
    }
    
    // Default to New User (same as Inactive for backwards compatibility)
    return (
      <div 
        className={`${baseClasses} rounded-full border-2 bg-transparent text-orange-500`}
        style={{ borderColor: '#FF6B00' }}
      >
        {initials}
      </div>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-10 w-10 hover:bg-accent/50 p-0"
        >
          {renderAvatarWithLevelShape()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 p-4" align="end">
        <div className="flex flex-col space-y-3">
          <div className="text-sm text-muted-foreground truncate">
            {user.email}
          </div>
          
          {/* User Level Display */}
          <div className="flex items-center gap-2">
            {isLoading ? (
              <span className="text-sm text-muted-foreground">Loading...</span>
            ) : (
              <>
                <UserLevelBadge 
                  level={currentLevel} 
                  size="md" 
                />
                <span className="text-sm font-medium">
                  {currentLevel} (Level {levelNumber})
                </span>
              </>
            )}
          </div>
        </div>
        
        <DropdownMenuSeparator className="my-3" />
        
        {onResetPassword && (
          <DropdownMenuItem onClick={onResetPassword} className="cursor-pointer">
            <Key className="mr-2 h-4 w-4" />
            Reset Password
          </DropdownMenuItem>
        )}
        
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {onDeleteAccount && (
          <DropdownMenuItem onClick={onDeleteAccount} className="cursor-pointer text-destructive">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Account
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserAvatarMenu;
